// Salesforce appends "- Now" to tariff picklist labels; strip before presence checks.
const SALESFORCE_TARIFF_SUFFIX = /-\s*Now/i

export function sanitizeTariffSchedule(value: string | null | undefined): string {
    return (value ?? '').replace(SALESFORCE_TARIFF_SUFFIX, '').trim()
}

// Looser normalization used only when hunting for a "did you mean" suggestion — it lets
// "a-1" suggest "A1", etc. The strict membership check (isKnownTariffSchedule) does NOT
// normalize: we want an exact match against what's in the DB, so users get a warning for
// near-misses and a suggestion pointing to the canonical code.
function normalizeForSuggestion(value: string): string {
    return value.toUpperCase().replace(/[\s\-._]/g, '')
}

export interface KnownTariff {
    schedule: string
    utilityShortName: string | null
}

export function isKnownTariffSchedule(value: string | null | undefined, knownTariffs: KnownTariff[]): boolean {
    if (value == null || value === '') return false
    return knownTariffs.some((t) => t.schedule === value)
}

// Classic iterative Levenshtein distance. Small input sizes (tariff codes are <=10 chars),
// so no perf concerns.
function levenshtein(a: string, b: string): number {
    if (a === b) return 0
    if (a.length === 0) return b.length
    if (b.length === 0) return a.length

    const prev: number[] = new Array(b.length + 1)
    const curr: number[] = new Array(b.length + 1)
    for (let j = 0; j <= b.length; j++) prev[j] = j

    for (let i = 1; i <= a.length; i++) {
        curr[0] = i
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1
            curr[j] = Math.min(
                curr[j - 1] + 1, // insertion
                prev[j] + 1, // deletion
                prev[j - 1] + cost // substitution
            )
        }
        for (let j = 0; j <= b.length; j++) prev[j] = curr[j]
    }
    return prev[b.length]
}

// Returns the closest tariff schedule if a reasonable match exists, otherwise null.
// Uses a loose normalization (case + separator insensitive) so e.g. "a-1" suggests "A1".
// Distance threshold scales with the length of the input to keep short codes from matching anything.
// When `utilityShortName` is provided, candidates scoped to that utility are preferred over others.
export function suggestClosestTariff(
    value: string,
    knownTariffs: KnownTariff[],
    utilityShortName?: string | null
): string | null {
    if (!value) return null
    const normalizedInput = normalizeForSuggestion(value)
    if (normalizedInput === '') return null

    const schedulesForUtility = new Set<string>()
    const allSchedules = new Set<string>()
    for (const t of knownTariffs) {
        allSchedules.add(t.schedule)
        if (utilityShortName != null && t.utilityShortName === utilityShortName) {
            schedulesForUtility.add(t.schedule)
        }
    }

    const findBestIn = (candidates: Iterable<string>): { schedule: string; distance: number } | null => {
        let best: { schedule: string; distance: number } | null = null
        for (const schedule of candidates) {
            if (schedule === value) return null // exact match — nothing to suggest
            const normalizedCandidate = normalizeForSuggestion(schedule)
            const distance = levenshtein(normalizedInput, normalizedCandidate)
            if (best == null || distance < best.distance) {
                best = { schedule, distance }
            }
        }
        return best
    }

    // Require the match to be meaningfully close. For very short codes (<=3 chars) allow
    // distance <= 1; otherwise allow up to 2 edits but never more than 1/3 of the length.
    const maxAllowed = normalizedInput.length <= 3 ? 1 : Math.min(2, Math.floor(normalizedInput.length / 3))

    // Prefer suggestions scoped to the record's utility so we don't suggest e.g. a PG&E
    // schedule when the record is for SCE.
    const scopedBest = schedulesForUtility.size > 0 ? findBestIn(schedulesForUtility) : null
    if (scopedBest != null && scopedBest.distance <= maxAllowed) return scopedBest.schedule

    const globalBest = findBestIn(allSchedules)
    if (globalBest != null && globalBest.distance <= maxAllowed) return globalBest.schedule
    return null
}

export interface TariffScheduleWarning {
    message: string
    suggestion: string | null
}

export function getTariffScheduleWarning(
    value: string | null | undefined,
    knownTariffs: KnownTariff[],
    utilityShortName?: string | null
): TariffScheduleWarning | null {
    if (value == null || value === '') return null
    if (knownTariffs.length === 0) return null

    const matches = knownTariffs.filter((t) => t.schedule === value)
    if (matches.length === 0) {
        // Unknown schedule code entirely.
        return {
            message: `"${value}" not found`,
            suggestion: suggestClosestTariff(value, knownTariffs, utilityShortName),
        }
    }

    // Known schedule, but not associated with the record's utility.
    if (
        utilityShortName != null &&
        utilityShortName !== '' &&
        !matches.some((t) => t.utilityShortName === utilityShortName)
    ) {
        return {
            message: `"${value}" doesn't appear to be associated with ${utilityShortName}`,
            suggestion: null,
        }
    }

    return null
}
